import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useDocumentData } from 'features/libra/document/DocumentDataContext'
import { isUnprocessableDocument } from 'features/libra/document/documentProcessingReady'
import { useCallback, useEffect, useMemo, useState } from 'react'

const EXCLUDED_CATEGORIES: ReadonlySet<string> = new Set([
  'zone_reference_images',
])

type ProcessingStats = Record<string, Record<string, number>>

/**
 * Checks whether all report entries (excluding categories in
 * `EXCLUDED_CATEGORIES` and file types not supported by the processing
 * pipeline) linked to this ticket's unit have been fully processed by the
 * cantilever-ai document processing pipeline.
 *
 * Returns `allProcessed: true` only when every relevant entry has zero
 * pending (`None`) and zero `failed` counts — i.e. fully processed.
 * Gracefully degrades to `allProcessed: true` on API errors so the button
 * is never permanently stuck.
 */
export function useDocumentProcessingReady(): {
  allProcessed: boolean
  isCheckingProcessing: boolean
  unprocessedCount: number
  failedEntryIds: string[]
  /** Entry ids are unchanged after a reprocess; this just re-queries stats. */
  refreshProcessingStatus: () => void
} {
  const { documentsByCategory, isLoading: docsLoading } = useDocumentData()
  const { authorizedAxios: axios } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()

  const [stats, setStats] = useState<ProcessingStats | null>(null)
  const [fetchDone, setFetchDone] = useState(false)
  const [refreshNonce, setRefreshNonce] = useState(0)

  const refreshProcessingStatus = useCallback(
    () => setRefreshNonce((n) => n + 1),
    []
  )

  const relevantEntryIds = useMemo(
    () =>
      [...documentsByCategory.entries()]
        .filter(([category]) => !EXCLUDED_CATEGORIES.has(category))
        .flatMap(([, docs]) =>
          docs.filter((d) => !isUnprocessableDocument(d)).map((d) => d.entry_id)
        ),
    [documentsByCategory]
  )

  useEffect(() => {
    if (docsLoading || relevantEntryIds.length === 0) {
      setFetchDone(!docsLoading)
      setStats(null)
      return
    }

    let cancelled = false
    setFetchDone(false)

    axios
      .post(
        `${ROLODEX_URL}/api/v2/cantilever-ai/document-processing/stats/query`,
        { entry_ids: relevantEntryIds }
      )
      .then((response) => {
        if (!cancelled) {
          setStats(response.data as ProcessingStats)
          setFetchDone(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStats(null)
          setFetchDone(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [relevantEntryIds, docsLoading, axios, ROLODEX_URL, refreshNonce])

  return useMemo(() => {
    if (relevantEntryIds.length === 0) {
      return {
        allProcessed: true,
        isCheckingProcessing: false,
        unprocessedCount: 0,
        failedEntryIds: [],
        refreshProcessingStatus,
      }
    }
    if (!fetchDone) {
      return {
        allProcessed: false,
        isCheckingProcessing: true,
        unprocessedCount: 0,
        failedEntryIds: [],
        refreshProcessingStatus,
      }
    }
    if (!stats) {
      return {
        allProcessed: true,
        isCheckingProcessing: false,
        unprocessedCount: 0,
        failedEntryIds: [],
        refreshProcessingStatus,
      }
    }

    let unprocessed = 0
    const failedEntryIds: string[] = []
    for (const entryId of relevantEntryIds) {
      const entryStat = stats[entryId]
      const pending = entryStat?.['None'] ?? 0
      const failed = entryStat?.failed ?? 0
      if (!entryStat || pending > 0 || failed > 0) {
        unprocessed++
      }
      if (failed > 0) {
        failedEntryIds.push(entryId)
      }
    }

    return {
      allProcessed: unprocessed === 0,
      isCheckingProcessing: false,
      unprocessedCount: unprocessed,
      failedEntryIds,
      refreshProcessingStatus,
    }
  }, [relevantEntryIds, stats, fetchDone, refreshProcessingStatus])
}
